import { studyQueryKeys } from '@/config/queryKeys';
import { STUDIES_QUERY_GC_TIME, STUDIES_QUERY_STALE_TIME, mainIconSize, server } from '@/constants';
import { dateComparator } from '@/helpers/dates.ts';
import { agGridDatasourceQueryKey, createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { getDateAsString } from '@/lib/dates.ts';
import { InstrumentRunName } from '@/pages/Instrument_Runs/components';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app.ts';
import {
  INSTRUMENT_RUN_ASSAY_VERSIONS,
  InstrumentRunPublic,
  InstrumentRunStatus,
  PaginatedInstrumentRunsResponse,
  PaginatedStudiesResponse,
} from '@/types';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import { Empty, QCBadge, StatusBadge } from '@components/index.ts';
import { Avatar } from '@nautilusbio/nb-design-system/Avatar';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { Plus } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import styles from '../../pages/Instrument_Runs/styles.module.css';
import studyStyles from '../../pages/Studies/styles.module.css';
import { createSetFilterParams } from '../../pages/Studies/utils/sharedColumnDefs';
import tableStyles from '../../styles/components/table.module.css';
import globalStyles from '../../styles/index.module.css';

const tableContainerClasses = { className: clsx(tableStyles.table, globalStyles.scrollbars) };

// Grid data refresh interval: 3 minutes (in milliseconds)
const GRID_REFRESH_INTERVAL_MS = 3 * 60 * 1000;

export const Route = createFileRoute('/instrument_runs/')({
  beforeLoad: ({ context }) => {
    // Redirect to login if user is not authenticated
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({
        to: '/login',
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('general');
  const navigate = useNavigate();
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization?.id));
  const gridRef = useRef<AgGridReact<InstrumentRunPublic>>(null);
  const shouldRefreshInstrumentRuns = useAppStore(state => state.shouldRefreshInstrumentRuns);
  const setShouldRefreshInstrumentRuns = useAppStore(state => state.setShouldRefreshInstrumentRuns);
  const [gridReady, setGridReady] = useState(false);

  // InstrumentRunType options from API spec

  const [colDefs] = useState<ColDef<InstrumentRunPublic>[]>([
    {
      cellClass: tableStyles.tableCellStart,
      cellRenderer: ({ data }: { data: InstrumentRunPublic }) => <InstrumentRunName data={data} />,
      field: 'name',
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith', 'blank', 'notBlank'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
      minWidth: 300,
    },
    {
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: ({ data: { status } }: { data: InstrumentRunPublic }) =>
        status && <StatusBadge className={styles.badgeInTable} size="xs" status={status as InstrumentRunStatus} />,
      field: 'status',
      headerName: 'Instrument Status',
      maxWidth: 160,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'blank', 'notBlank'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
    },
    {
      cellClass: tableStyles.tableCellCenter,
      field: 'decode_status',
      headerName: 'Decode Status',
      maxWidth: 160,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'blank', 'notBlank'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
    },
    {
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: ({ data: { sample_qc } }: { data: InstrumentRunPublic }) => <QCBadge qc={sample_qc} />,
      field: 'sample_qc',
      headerName: 'Sample QC',
      maxWidth: 130,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'blank', 'notBlank'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
    },
    {
      cellClass: tableStyles.tableCellCenter,
      field: 'assay_version',
      headerName: 'Assay',
      maxWidth: 200,
      filter: 'agSetColumnFilter',
      filterParams: createSetFilterParams('assay_version', INSTRUMENT_RUN_ASSAY_VERSIONS),
    },
    {
      cellClass: tableStyles.tableCellCenter,
      field: 'num_samples',
      headerName: 'Samples',
      maxWidth: 110,
    },
    {
      cellClass: tableStyles.tableCellCenter,
      field: 'species',
      maxWidth: 140,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'blank', 'notBlank'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
    },
    {
      cellClass: tableStyles.tableCellCenter,
      field: 'instrument_name',
      headerName: 'Instrument',
      maxWidth: 160,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'blank', 'notBlank'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
    },
    {
      cellClass: tableStyles.tableCellCenter,
      comparator: dateComparator,
      field: 'started_at',
      filter: 'agDateColumnFilter',
      headerName: 'Run Started',
      maxWidth: 210,
      valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : ''),
    },
    {
      cellClass: tableStyles.tableCellCenter,
      comparator: dateComparator,
      field: 'completed_at',
      filter: 'agDateColumnFilter',
      headerName: 'Run Completed',
      maxWidth: 210,
      valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : ''),
    },
    {
      cellClass: tableStyles.tableCellStart,
      comparator: dateComparator,
      field: 'created_at',
      filter: 'agDateColumnFilter',
      headerName: t('created_at'),
      hide: true,
      maxWidth: 210,
      valueFormatter: ({ value }) => getDateAsString(new Date(value)),
    },
    {
      field: 'updated_at',
      hide: true,
      comparator: dateComparator,
      filter: 'agDateColumnFilter',
    },
    {
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: ({ data: { created_by_full_name } }: { data: InstrumentRunPublic }) => (
        <Tooltip
          content={created_by_full_name}
          placement="right-start"
          trigger={
            <Avatar className={styles.avatar} size="xs3">
              {created_by_full_name}
            </Avatar>
          }
        />
      ),
      field: 'created_by_full_name',
      headerName: t('created_by'),
      hide: true,
      maxWidth: 130,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'blank', 'notBlank'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
    },
    {
      field: 'updated_by_full_name',
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'blank', 'notBlank'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
      hide: true,
    },
    {
      field: 'submitted_at',
      hide: true,
      comparator: dateComparator,
      filter: 'agDateColumnFilter',
    },
    {
      field: 'submitted_by_full_name',
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'blank', 'notBlank'],
        maxNumConditions: 2,
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
      hide: true,
    },
    {
      cellClass: tableStyles.tableCellStart,
      field: 'description',
      filter: false,
      hide: true,
      maxWidth: 400,
    },
  ]);

  const { data: studiesData } = useSuspenseQuery<PaginatedStudiesResponse>({
    queryKey: selectedOrg ? studyQueryKeys.list(selectedOrg) : ['disabled-studies-query'],
    queryFn: async () => {
      if (!selectedOrg) {
        return { records: [], total: 0, skip: 0, limit: 0, has_more: false };
      }
      return searchStudies(selectedOrg, {
        skip: 0,
        limit: 500,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
    },
    staleTime: STUDIES_QUERY_STALE_TIME,
    gcTime: STUDIES_QUERY_GC_TIME,
  });

  const hasNoStudies = studiesData?.total === 0;

  // Memoized datasource for AG Grid server-side model
  const datasource = useMemo<IServerSideDatasource>(
    () =>
      createAgGridDatasource<InstrumentRunPublic, PaginatedInstrumentRunsResponse>(
        `${server}/orgs/${selectedOrg}/instrument_runs/search`,
        undefined,
        { staleTime: 60_000 },
      ),
    [selectedOrg],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  // Periodic refresh of the grid data (every 3 minutes)
  useEffect(() => {
    if (!selectedOrg || !gridRef.current?.api) return;

    // Initial refresh on mount
    gridRef.current.api.refreshServerSide({ purge: true });

    // Set up periodic refresh every 3 minutes
    const intervalId = setInterval(() => {
      if (gridRef.current?.api) {
        gridRef.current.api.refreshServerSide({ purge: true });
      }
    }, GRID_REFRESH_INTERVAL_MS); // 3 minutes in milliseconds

    return () => clearInterval(intervalId);
  }, [selectedOrg]);

  // Refresh grid if Zustand flag is set, then clear the flag
  // Invalidate agGridDatasourceQueryKey or use useAgGridDatasourceRefresh before AG Grid refresh
  useEffect(() => {
    if (shouldRefreshInstrumentRuns && gridReady && gridRef.current?.api && selectedOrg) {
      // Invalidate TanStack Query cache for AG Grid datasource before refreshing
      // If you have useAgGridDatasourceRefresh, use it here instead
      if (queryClient !== undefined && typeof agGridDatasourceQueryKey === 'function') {
        queryClient.invalidateQueries({
          queryKey: agGridDatasourceQueryKey(`${server}/orgs/${selectedOrg}/instrument_runs/search`),
        });
      }
      gridRef.current.api.refreshServerSide({ purge: true });
      setShouldRefreshInstrumentRuns(false);
    }
  }, [queryClient, shouldRefreshInstrumentRuns, selectedOrg, setShouldRefreshInstrumentRuns, gridReady]);

  return (
    <Stack className={studyStyles.container} orientation="vertical">
      <Stack
        alignItems="center"
        className={studyStyles.newTitleRow}
        justifyContent="space-between"
        orientation="horizontal"
      >
        <div />
        <Button
          data-testid="create-new-run-btn"
          isDisabled={hasNoStudies}
          onPress={() => navigate({ to: '/instrument_runs/new' })}
          size="md"
          visual="unstyled"
        >
          <Plus size={mainIconSize} />
          {t('create_new_run', { ns: 'runs' })}
        </Button>
      </Stack>

      {hasNoStudies ? (
        <Empty
          body={
            <Stack orientation="vertical" gap={5} alignItems="center">
              <span>{t('no_studies_message', { ns: 'runs' })}</span>
              <Button
                data-testid="create-study-from-runs-btn"
                onPress={() => navigate({ to: '/studies', search: { openCreate: true } })}
                size="md"
                visual="primary"
              >
                {t('create_study', { ns: 'runs' })}
              </Button>
            </Stack>
          }
          className={styles.empty}
          title={t('no_runs', { ns: 'runs' })}
        />
      ) : (
        <div className={studyStyles.tableContainer}>
          <Table
            columnDefs={colDefs}
            container={tableContainerClasses}
            domLayout="normal"
            onGridReady={e => {
              setGridReady(true);

              e.api.setGridOption('serverSideDatasource', datasource);
              sizeColumnsOnFirstVisit(e.api, 'instrument-runs-table');

              // If refresh flag was set before grid was ready, handle it now
              if (shouldRefreshInstrumentRuns && selectedOrg) {
                if (queryClient !== undefined && typeof agGridDatasourceQueryKey === 'function') {
                  queryClient.invalidateQueries({
                    queryKey: agGridDatasourceQueryKey(`${server}/orgs/${selectedOrg}/instrument_runs/search`),
                  });
                }
                e.api.refreshServerSide({ purge: true });
                setShouldRefreshInstrumentRuns(false);
              }
            }}
            density="normal"
            cacheBlockSize={100}
            pagination
            tableId="instrument-runs-table"
            enableResponsiveResize={false}
            onGridPreDestroyed={e => flushColumnState(e.api, 'instrument-runs-table')}
            paginationAutoPageSize
            ref={gridRef}
            rowModelType="serverSide"
          />
        </div>
      )}
    </Stack>
  );
}
